import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { clsx } from 'clsx'
import {
  X, ChevronLeft, ChevronRight, Info, Target, ListChecks, Code2,
  CheckCircle2, Wrench, Clock, ArrowUpRight, BookOpen, Link2,
} from 'lucide-react'
import {
  getKbEntry, KB_CATEGORIES, IMPACT_META, DIFFICULTY_META, type KbEntry,
} from '../data/knowledgeBase'
import { helpPath } from '../lib/localizedUrl'

interface Props {
  /** Pořadí id pro listování Předchozí/Další (typicky doporučení aktuálního výsledku). */
  ids: string[]
  /** Které id otevřít jako první. */
  startId: string
  onClose: () => void
}

const impactColor: Record<string, string> = {
  red:    'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  orange: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300',
  amber:  'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  gray:   'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300',
  green:  'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300',
}

export default function KnowledgeModal({ ids, startId, onClose }: Props) {
  const { t, i18n } = useTranslation()
  const [currentId, setCurrentId] = useState(startId)

  const entry: KbEntry | undefined = getKbEntry(currentId)
  const pos = ids.indexOf(currentId)
  const hasList = pos !== -1 && ids.length > 1

  const go = useCallback((dir: number) => {
    if (pos === -1) return
    const next = (pos + dir + ids.length) % ids.length
    setCurrentId(ids[next])
  }, [pos, ids])

  // Klávesy: šipky listují, Esc zavírá
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') go(1)
      else if (e.key === 'ArrowLeft') go(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [go, onClose])

  // Scroll nahoru při změně tématu
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null)
  useEffect(() => { scrollEl?.scrollTo({ top: 0 }) }, [currentId, scrollEl])

  if (!entry) return null

  const cat = KB_CATEGORIES[entry.category]
  const impact = IMPACT_META[entry.impact]
  const diff = DIFFICULTY_META[entry.difficulty]

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="relative w-full max-w-2xl max-h-[88vh] flex flex-col rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-100 dark:border-white/10 overflow-hidden"
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ type: 'spring', damping: 26, stiffness: 280 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="shrink-0 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-white/10 bg-gradient-to-br from-gray-50 to-white dark:from-white/5 dark:to-transparent">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={clsx('text-[11px] font-semibold px-2.5 py-1 rounded-full',
                  'bg-primary-100 text-primary-700 dark:bg-primary-500/15 dark:text-primary-300')}>
                  {cat.label}
                </span>
                <span className={clsx('inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full', impactColor[impact.color])}>
                  <Target className="w-3 h-3" /> {impact.label}
                </span>
                <span className={clsx('inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full', impactColor[diff.color])}>
                  <Wrench className="w-3 h-3" /> {diff.label}
                </span>
              </div>
              <button onClick={onClose} className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-white/10 transition" aria-label={t('common.close')}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white leading-snug">{entry.title}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{entry.short}</p>
            {entry.effort && (
              <p className="inline-flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 mt-2">
                <Clock className="w-3.5 h-3.5" /> {t('kb.effort')}: {entry.effort}
              </p>
            )}
          </div>

          {/* Body */}
          <div ref={setScrollEl} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            <Section icon={<Info className="w-4 h-4" />} color="indigo" title={t('kb.what')}>
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{entry.what}</p>
            </Section>

            <Section icon={<Target className="w-4 h-4" />} color="rose" title={t('kb.why')}>
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{entry.why}</p>
            </Section>

            <Section icon={<ListChecks className="w-4 h-4" />} color="emerald" title={t('kb.how')}>
              <ol className="space-y-2">
                {entry.how.map((step, i) => (
                  <li key={i} className="flex gap-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </Section>

            {entry.examples && entry.examples.length > 0 && (
              <Section icon={<Code2 className="w-4 h-4" />} color="violet" title={t('kb.example')}>
                <div className="space-y-3">
                  {entry.examples.map((ex, i) => (
                    <div key={i}>
                      {ex.caption && <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">{ex.caption}</p>}
                      {ex.bad && (
                        <CodeBlock label={t('kb.bad')} tone="bad">{ex.bad}</CodeBlock>
                      )}
                      {ex.good && (
                        <CodeBlock label={t('kb.good')} tone="good">{ex.good}</CodeBlock>
                      )}
                      {ex.code && <CodeBlock tone="neutral">{ex.code}</CodeBlock>}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Závěr — zvýrazněný */}
            <div className="rounded-xl border border-primary-200 dark:border-primary-500/30 bg-primary-50/70 dark:bg-primary-500/10 p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <CheckCircle2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                <h3 className="text-sm font-bold text-primary-700 dark:text-primary-300">{t('kb.conclusion')}</h3>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">{entry.conclusion}</p>
            </div>

            {/* Související */}
            {entry.related && entry.related.length > 0 && (
              <div>
                <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
                  <Link2 className="w-3.5 h-3.5" /> {t('kb.related')}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {entry.related.map((rid) => {
                    const r = getKbEntry(rid)
                    if (!r) return null
                    return (
                      <button key={rid} onClick={() => setCurrentId(rid)}
                        className="text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-primary-100 hover:text-primary-700 dark:hover:bg-primary-500/20 transition">
                        {r.title}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer — navigace */}
          <div className="shrink-0 px-5 py-3 border-t border-gray-100 dark:border-white/10 bg-gray-50/80 dark:bg-white/5 flex items-center justify-between gap-3">
            <button
              onClick={() => go(-1)}
              disabled={!hasList}
              className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft className="w-4 h-4" /> {t('common.prev')}
            </button>

            <div className="flex items-center gap-3">
              {hasList && (
                <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">{pos + 1} / {ids.length}</span>
              )}
              <Link to={helpPath(entry.id, i18n.language)} onClick={onClose}
                className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline">
                <BookOpen className="w-3.5 h-3.5" /> {t('kb.full_docs')} <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>

            <button
              onClick={() => go(1)}
              disabled={!hasList}
              className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition"
            >
              {t('common.next')} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

function Section({ icon, color, title, children }: { icon: React.ReactNode; color: string; title: string; children: React.ReactNode }) {
  const colors: Record<string, string> = {
    indigo:  'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300',
    rose:    'bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300',
    emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300',
    violet:  'bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300',
  }
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className={clsx('w-7 h-7 rounded-lg flex items-center justify-center', colors[color])}>{icon}</span>
        <h3 className="text-sm font-bold text-gray-900 dark:text-white">{title}</h3>
      </div>
      <div className="pl-9">{children}</div>
    </div>
  )
}

function CodeBlock({ label, tone, children }: { label?: string; tone: 'bad' | 'good' | 'neutral'; children: string }) {
  const tones = {
    bad:     'border-red-200 dark:border-red-500/30 bg-red-50/60 dark:bg-red-500/5',
    good:    'border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/5',
    neutral: 'border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5',
  }
  const labelTones = {
    bad: 'text-red-600 dark:text-red-400',
    good: 'text-emerald-600 dark:text-emerald-400',
    neutral: 'text-gray-500',
  }
  return (
    <div className={clsx('rounded-lg border mt-1.5 overflow-hidden', tones[tone])}>
      {label && <div className={clsx('px-3 pt-2 text-[10px] font-bold uppercase tracking-wider', labelTones[tone])}>{label}</div>}
      <pre className="px-3 py-2 text-xs font-mono text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre-wrap break-words leading-relaxed"><code>{children}</code></pre>
    </div>
  )
}
